import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface UserAccessStatus {
  isActive: boolean;
  isSuspended: boolean;
  accessExpired: boolean;
  suspendedUntil: string | null;
  accessExpiresAt: string | null;
  suspensionReason: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  accessStatus: UserAccessStatus | null;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, name: string) => Promise<{ data: { user: User | null; session: Session | null } | null; error: Error | null }>;
  signInWithGoogle: () => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [accessStatus, setAccessStatus] = useState<UserAccessStatus | null>(null);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        // Defer checks to avoid Supabase deadlock
        if (session?.user) {
          setTimeout(() => {
            checkAdminRole(session.user.id);
            checkAccessStatus(session.user.id);
            // Record only on a fresh sign-in, not on token refresh.
            if (event === 'SIGNED_IN') {
              supabase.rpc('record_login').then(({ error }) => {
                if (error) console.error('record_login failed:', error.message);
              });
            }
          }, 0);
        } else {
          setIsAdmin(false);
          setAccessStatus(null);
        }
      }
    );

    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);

        if (session?.user) {
          checkAdminRole(session.user.id);
          checkAccessStatus(session.user.id);
        }
      })
      .catch((err) => {
        console.error('Supabase getSession failed:', err);
        setLoading(false);
      });

    return () => subscription.unsubscribe();
  }, []);

  const checkAdminRole = async (userId: string) => {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .eq('role', 'admin')
      .maybeSingle();
    
    if (!error && data) {
      setIsAdmin(true);
    } else {
      setIsAdmin(false);
    }
  };

  const checkAccessStatus = async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('is_active, suspended_until, access_expires_at, suspension_reason')
      .eq('id', userId)
      .single();

    if (error || !data) {
      setAccessStatus(null);
      return;
    }

    const now = new Date();
    const suspendedUntil = data.suspended_until ? new Date(data.suspended_until) : null;
    const accessExpiresAt = data.access_expires_at ? new Date(data.access_expires_at) : null;

    setAccessStatus({
      isActive: data.is_active ?? true,
      isSuspended: suspendedUntil ? suspendedUntil > now : false,
      accessExpired: accessExpiresAt ? accessExpiresAt <= now : false,
      suspendedUntil: data.suspended_until,
      accessExpiresAt: data.access_expires_at,
      suspensionReason: data.suspension_reason,
    });
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signUp = async (email: string, password: string, name: string) => {
    const emailRedirectTo = `${window.location.origin}/dashboard`;
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo,
        data: { name },
      },
    });
    return { data, error };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/dashboard`,
      },
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setIsAdmin(false);
    setAccessStatus(null);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isAdmin, accessStatus, signIn, signUp, signInWithGoogle, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
